import { useCallback, useEffect, useState } from 'react';

/**
 * Тема оформления: тёмная, светлая или та, что выбрана в системе.
 *
 * Цвета обеих тем уже описаны в `styles/tokens.css` — здесь только выбор
 * между ними. Тёмная по §12 основная, светлая вторичная, и переключатель
 * этого порядка не меняет: он лишь позволяет уйти в светлую тому, кто
 * сидит не в серверной, а у окна.
 *
 * Выбор — свойство того, кто смотрит, а не организации: один и тот же
 * инженер с ноутбука в цеху и с домашнего монитора хочет разного. Отсюда
 * хранилище — браузер, как и у свёрнутого меню (см. `shellPreferences`).
 */
export type Theme = 'dark' | 'light';

/** `null` — «как в системе»: человек переключатель ещё не трогал. */
export type ThemeChoice = Theme | null;

const THEME_KEY = 'netdeck.shell.theme';

/**
 * Что выбрал человек.
 *
 * Значение, которого мы не писали, считается отсутствующим: сюда попадает
 * и мусор от прошлых версий, и правка руками в консоли, и в обоих случаях
 * честнее вернуться к системной настройке, чем спорить с браузером.
 */
export function readThemeChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_KEY);

    if (stored === 'dark' || stored === 'light') {
      return stored;
    }
  } catch {
    // Хранилище недоступно — приватный режим, запрет сторонних данных.
    // Тему покажем системную: она есть всегда.
  }

  return null;
}

function writeThemeChoice(choice: ThemeChoice): void {
  try {
    if (choice === null) {
      localStorage.removeItem(THEME_KEY);
    } else {
      localStorage.setItem(THEME_KEY, choice);
    }
  } catch {
    // Не сохранилось — на этом экране тема всё равно уже применена,
    // потеряется только память о выборе.
  }
}

/** Тема, выбранная в системе. Без `matchMedia` (jsdom, старые браузеры) — тёмная. */
export function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Применить выбор к документу.
 *
 * Тема живёт атрибутом `data-theme` на `<html>`, а не классом на React-корне:
 * цвет фона задан у `html` и `body` (см. `styles/index.css`), и поля вокруг
 * приложения — полосы прокрутки, область за резинкой прокрутки — красятся
 * только оттуда. Отсутствие атрибута означает «как в системе»: за это
 * отвечает `prefers-color-scheme` в токенах, и подставлять сюда вычисленную
 * тему нельзя — иначе выбор системы перестал бы доходить до приложения
 * на живой странице.
 */
export function applyTheme(choice: ThemeChoice): void {
  if (choice === null) {
    document.documentElement.removeAttribute('data-theme');
  } else {
    document.documentElement.setAttribute('data-theme', choice);
  }
}

/**
 * Тема и переключение между тёмной и светлой.
 *
 * Переключатель на два положения, а не на три: «как в системе» остаётся
 * состоянием по умолчанию, но отдельной кнопки не получает. Третье положение
 * в шапке пришлось бы объяснять подписью, а объяснять там негде — при том
 * что нажимают такую кнопку раз в полгода.
 */
export function useTheme(): { theme: Theme; toggle: () => void } {
  const [choice, setChoice] = useState<ThemeChoice>(readThemeChoice);
  const [system, setSystem] = useState<Theme>(systemTheme);

  /*
    Пока выбора нет, тему задаёт система — и задаёт её в том числе на живой
    странице: ночной режим включается по расписанию, и приложение обязано
    потемнеть вместе со всем остальным, а не до следующей перезагрузки.
  */
  useEffect(() => {
    const query = window.matchMedia?.('(prefers-color-scheme: light)');

    if (!query) {
      return;
    }

    const onChange = (): void => setSystem(query.matches ? 'light' : 'dark');

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const theme = choice ?? system;

  const toggle = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';

    setChoice(next);
    writeThemeChoice(next);
    applyTheme(next);
  }, [theme]);

  return { theme, toggle };
}
